import React, {
  useState, KeyboardEvent, ChangeEvent
} from 'react';
import {
  Box,
  Flex,
  Text,
  InputLabel,
  InputDescription,
} from '@huspy/briks-web';
import { validators } from '@huspy/forge/shared';
import { useTranslation } from 'react-i18next';
import { Cancel } from '@huspy/briks-icons';

const { emailFieldValidator } = validators;

interface EmailRecipientsProps {
  name: string;
  label?: string;
  allowMultiple?: boolean;
  defaultEmail?: string;
  disabled?: boolean;
  onValueChange: Function;
  fromErrors?: string;
}

export const EmailRecipientsInput: React.FC<EmailRecipientsProps> = ({
  name,
  label,
  allowMultiple = true,
  defaultEmail = '',
  disabled = false,
  onValueChange,
  fromErrors,
}) => {
  const [emails, setEmails] = useState<string[]>(
    defaultEmail ? [defaultEmail] : []
  );
  const [inputValue, setInputValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();

  const addEmail = () => {
    try {
      const emailToAdd = inputValue.trim();
      if (
        emailToAdd
        && !emails.includes(emailToAdd)
        && (allowMultiple || emails.length === 0)
      ) {
        emailFieldValidator.parse(inputValue);
        const newEmails = [...emails, emailToAdd];
        setEmails(newEmails);
        onValueChange(newEmails);
      }
      setInputValue('');
      setError('');
    } catch {
      setError(t('common.validation.invalidEmail'));
    }
  };

  const removeEmail = (index: number) => {
    if (!inputValue && emails.length > 0) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addEmail();
    }
    if (event.key === 'Backspace') {
      removeEmail(emails.length - 1);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const inputStyle: React.CSSProperties = {
    flexGrow: 1,
    border: 'none',
    padding: '5px',
    outline: 'none',
    minWidth: '120px',
  };

  const hasErrors = !!error || !!fromErrors;
  return (
    <Box>
      <InputLabel isError={ hasErrors }>{label}</InputLabel>
      <Flex
        wrap='wrap'
        className='input'
        borderColor={ hasErrors ? 'error.900' : '' }
      >
        {emails.map((email, index) => (
          <Flex
            key={ email }
            h='30px'
            bg='base.white'
            borderWidth='1'
            borderColor='neutral.200'
            borderRadius='4'
            align='center'
            px='3'
            py='2'
          >
            <Text color='neutral.500'>{email}</Text>
            {!disabled && (
              <Box ml='2' onClick={ () => removeEmail(index) } cursor='pointer'>
                <Cancel width='15' height='15' />
              </Box>
            )}
          </Flex>
        ))}
        {!disabled && (
          <>
            <input
              name={ name }
              type='text'
              value={ inputValue }
              onChange={ handleChange }
              onKeyDown={ handleKeyPress }
              onBlur={ addEmail }
              style={ inputStyle }
              disabled={ !allowMultiple && emails.length > 0 }
            />
          </>
        )}
      </Flex>
      <InputDescription isError={ hasErrors }>{error || fromErrors}</InputDescription>
    </Box>
  );
};
